export function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatDateTime(dateStr: string): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Antigüedad de un instante en palabras: «hace 5 min», «hace 3 h», «ayer»,
 * «hace 12 días», «hace 4 meses».
 *
 * Los días se cuentan por fecha del calendario local y no por bloques de 24 h,
 * para que algo de anoche a las 23:00 sea «ayer» y no «hace 10 h».
 */
export function haceCuanto(iso: string | null | undefined): string {
  if (!iso) return '';
  const fecha = new Date(iso);
  const ahora = new Date();
  const minutos = Math.floor((ahora.getTime() - fecha.getTime()) / 60_000);
  if (!Number.isFinite(minutos)) return '';
  if (minutos < 1) return 'ahora mismo';
  if (minutos < 60) return `hace ${minutos} min`;

  const diaDe = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  // Redondeo y no división exacta: los días del cambio de hora duran 23 o 25 h.
  const dias = Math.round((diaDe(ahora) - diaDe(fecha)) / 86_400_000);
  if (dias <= 0) return `hace ${Math.floor(minutos / 60)} h`;
  if (dias === 1) return 'ayer';
  if (dias < 30) return `hace ${dias} días`;
  if (dias < 365) {
    const meses = Math.max(1, Math.round(dias / 30.44));
    return meses === 1 ? 'hace 1 mes' : `hace ${meses} meses`;
  }
  const anios = Math.floor(dias / 365.25);
  return anios <= 1 ? 'hace 1 año' : `hace ${anios} años`;
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
  }).format(amount);
}

export function getInitials(firstName: string, lastName: string): string {
  return `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase();
}

export function classNames(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

export const ZONE_LABELS: Record<string, string> = {
  barbate: 'Barbate',
  los_canos: 'Los Caños de Meca',
  zahara: 'Zahara de los Atunes',
  atlanterra: 'Atlanterra',
  tarifa: 'Tarifa',
  alicante: 'Alicante',
  otro: 'Otro',
};

export const SOURCE_LABELS: Record<string, string> = {
  idealista: 'Idealista',
  referido: 'Referido',
  puerta_fria: 'Puerta fría',
  redes_sociales: 'Redes sociales',
  facebook: 'Facebook Ads',
  web: 'Web',
  excel_import: 'Importación Excel',
  otro: 'Otro',
};

export const LANGUAGE_LABELS: Record<string, string> = {
  es: 'Español',
  en: 'Inglés',
  de: 'Alemán',
  fr: 'Francés',
  pt: 'Portugués',
};

export const PROPERTY_STATE_LABELS: Record<string, string> = {
  empty: 'Vacía',
  owner_use: 'Uso del propietario',
  long_term_rental: 'Alquiler largo plazo',
  already_tourist_rental: 'Ya alquiler turístico',
};

export const LICENSE_STATUS_LABELS: Record<string, string> = {
  active: 'Activa',
  pending: 'Pendiente',
  expired: 'Expirada',
  none: 'Sin licencia',
};

/**
 * Celda de CSV para abrir en Excel en español.
 *
 * Neutraliza la inyección de fórmulas: los campos que vienen de formularios
 * públicos son texto libre, y una celda que empieza por `=`, `+`, `@` o `-` la
 * ejecuta Excel al abrir el fichero. Anteponer una comilla simple la convierte
 * en texto sin que se vea al leerla.
 */
export function csvCell(value: string | number | undefined | null): string {
  const raw = value === undefined || value === null ? '' : String(value);
  const safe = /^[=+@]|^-(?!\d)/.test(raw) ? `'${raw}` : raw;
  return /[";\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/**
 * Filas a CSV con `;` y BOM. El punto y coma porque el Excel en español separa
 * así —con comas, el fichero se abre en una sola columna— y el BOM para que no
 * se coma las tildes.
 */
export function toCsv(rows: (string | number | undefined | null)[][]): string {
  return '\ufeff' + rows.map((r) => r.map(csvCell).join(';')).join('\r\n');
}
